/**
 * NEC 2023 Chapter 9 conduit data.
 *
 * Table 1  — allowable percent of conduit interior fill by conductor count.
 * Table 4  — total interior cross-sectional area (in², "100%" column) per
 *            conduit type and trade size.
 *
 * Values transcribed from NEC 2023 Ch.9 Tables 1 & 4. Pinned by golden tests.
 */

export type ConduitType = "EMT" | "RMC" | "IMC" | "PVC40" | "PVC80";

export const CONDUIT_TYPE_LABELS: Record<ConduitType, string> = {
  EMT: "EMT",
  RMC: "Rigid Metal Conduit (RMC)",
  IMC: "Intermediate Metal Conduit (IMC)",
  PVC40: "PVC Schedule 40",
  PVC80: "PVC Schedule 80",
};

/** Trade sizes we support, in order, as strings keyed in Table 4. */
export const TRADE_SIZES = [
  "1/2",
  "3/4",
  "1",
  "1-1/4",
  "1-1/2",
  "2",
  "2-1/2",
  "3",
  "3-1/2",
  "4",
] as const;

export type TradeSize = (typeof TRADE_SIZES)[number];

/**
 * Table 1 — allowable fill as a fraction of total interior area:
 *   1 conductor   → 53%
 *   2 conductors  → 31%
 *   over 2 (3+)   → 40%
 */
export function allowableFillFraction(conductorCount: number): number {
  if (conductorCount <= 0) return 0;
  if (conductorCount === 1) return 0.53;
  if (conductorCount === 2) return 0.31;
  return 0.4;
}

/** Table 4 — total interior area (in², 100% column) by conduit type & trade size. */
export const TABLE_4_TOTAL_AREA_IN2: Record<ConduitType, Record<TradeSize, number>> = {
  EMT: {
    "1/2": 0.304,
    "3/4": 0.533,
    "1": 0.864,
    "1-1/4": 1.496,
    "1-1/2": 2.036,
    "2": 3.356,
    "2-1/2": 5.858,
    "3": 8.846,
    "3-1/2": 11.545,
    "4": 14.753,
  },
  RMC: {
    "1/2": 0.314,
    "3/4": 0.549,
    "1": 0.887,
    "1-1/4": 1.526,
    "1-1/2": 2.071,
    "2": 3.408,
    "2-1/2": 4.866,
    "3": 7.499,
    "3-1/2": 10.01,
    "4": 12.882,
  },
  IMC: {
    "1/2": 0.342,
    "3/4": 0.586,
    "1": 0.959,
    "1-1/4": 1.647,
    "1-1/2": 2.225,
    "2": 3.63,
    "2-1/2": 5.135,
    "3": 7.922,
    "3-1/2": 10.584,
    "4": 13.631,
  },
  PVC40: {
    "1/2": 0.285,
    "3/4": 0.508,
    "1": 0.832,
    "1-1/4": 1.453,
    "1-1/2": 1.986,
    "2": 3.291,
    "2-1/2": 4.695,
    "3": 7.268,
    "3-1/2": 9.737,
    "4": 12.554,
  },
  PVC80: {
    "1/2": 0.217,
    "3/4": 0.409,
    "1": 0.688,
    "1-1/4": 1.237,
    "1-1/2": 1.711,
    "2": 2.874,
    "2-1/2": 4.119,
    "3": 6.442,
    "3-1/2": 8.688,
    "4": 11.258,
  },
};

export function conduitTotalAreaIn2(type: ConduitType, size: TradeSize): number {
  return TABLE_4_TOTAL_AREA_IN2[type][size];
}
